import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Bug, Settings, Activity, Database, Zap } from 'lucide-react'
import DebugPanel from './DebugPanel'
import logger from '../../utils/logger'

interface DebugTriggerProps {
  enabled?: boolean
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
}

const DebugTrigger: React.FC<DebugTriggerProps> = ({ 
  enabled = process.env.NODE_ENV === 'development',
  position = 'bottom-right'
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [stats, setStats] = useState(logger.getStats())

  // Update stats periodically
  useEffect(() => {
    if (!enabled) return

    const interval = setInterval(() => {
      setStats(logger.getStats())
    }, 2000)

    return () => clearInterval(interval)
  }, [enabled])

  // Keyboard shortcut to toggle debug panel
  useEffect(() => {
    if (!enabled) return

    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl+Shift+D to toggle debug panel
      if (event.ctrlKey && event.shiftKey && event.key === 'D') {
        event.preventDefault()
        setIsOpen(!isOpen)
        logger.userAction('toggle-debug-panel', 'DebugTrigger')
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [enabled, isOpen])

  if (!enabled) return null

  const positionClasses = {
    'top-left': 'top-4 left-4',
    'top-right': 'top-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'bottom-right': 'bottom-4 right-4'
  }

  const getRecentErrorCount = () => {
    const recentLogs = logger.getLogs().slice(-50) // Last 50 logs
    return recentLogs.filter(log => log.level >= 2).length // WARN, ERROR, CRITICAL
  }

  const recentErrors = getRecentErrorCount()

  return (
    <>
      <motion.div
        className={`fixed ${positionClasses[position]} z-40`}
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="relative">
          {/* Main Debug Button */}
          <motion.button
            onClick={() => setIsOpen(true)}
            onMouseEnter={() => setIsExpanded(true)}
            onMouseLeave={() => setIsExpanded(false)}
            className="bg-terra-500 hover:bg-terra-700 text-white rounded-full p-3 shadow-caderno-lg transition-colors relative"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title="Debug Panel (Ctrl+Shift+D)"
          >
            <Bug className="w-5 h-5" />
            
            {/* Error Badge */}
            {recentErrors > 0 && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-1 -right-1 bg-blood-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold"
              >
                {recentErrors > 9 ? '9+' : recentErrors}
              </motion.div>
            )}
          </motion.button>

          {/* Expanded Info Panel */}
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9, x: position.includes('right') ? 10 : -10 }}
                animate={{ opacity: 1, scale: 1, x: 0 }}
                exit={{ opacity: 0, scale: 0.9, x: position.includes('right') ? 10 : -10 }}
                className={`absolute ${
                  position.includes('right') ? 'right-full mr-3' : 'left-full ml-3'
                } ${
                  position.includes('top') ? 'top-0' : 'bottom-0'
                } bg-surface dark:bg-ink-700 rounded-caderno-sm shadow-caderno-xl border border-cream-400 dark:border-ink-500 p-4 min-w-64`}
              >
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Bug className="w-4 h-4 text-terra-500" />
                    <h3 className="font-semibold text-ink-900 dark:text-cream-50">
                      Debug Info
                    </h3>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-ink-500 dark:text-ink-300">Total Logs:</span>
                      <span className="font-medium text-ink-900 dark:text-cream-50">
                        {stats.totalLogs}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-ink-500 dark:text-ink-300">Recent Errors:</span>
                      <span className={`font-medium ${
                        recentErrors > 0 ? 'text-blood-500 dark:text-blood-500' : 'text-moss-500 dark:text-moss-500'
                      }`}>
                        {recentErrors}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-ink-500 dark:text-ink-300">Session:</span>
                      <span className="font-mono text-xs text-ink-900 dark:text-cream-50">
                        {stats.sessionId.slice(-8)}
                      </span>
                    </div>

                    {stats.userId && (
                      <div className="flex items-center justify-between">
                        <span className="text-ink-500 dark:text-ink-300">User:</span>
                        <span className="font-mono text-xs text-ink-900 dark:text-cream-50">
                          {stats.userId.slice(-8)}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="border-t border-cream-400 dark:border-ink-500 pt-3">
                    <div className="text-xs text-ink-400 dark:text-ink-300 space-y-1">
                      <div>Categories:</div>
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(stats.logsByCategory).map(([category, count]) => (
                          <span
                            key={category}
                            className="px-2 py-1 bg-cream-100 dark:bg-ink-700 rounded-caderno-xs text-xs"
                          >
                            {category}: {count}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-cream-400 dark:border-ink-500 pt-3">
                    <div className="text-xs text-ink-400 dark:text-ink-300 space-y-1">
                      <div>Levels:</div>
                      <div className="space-y-1">
                        {Object.entries(stats.logsByLevel).map(([level, count]) => (
                          <div key={level} className="flex items-center justify-between">
                            <span className="capitalize">{level.toLowerCase()}:</span>
                            <span className="font-medium">{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-cream-400 dark:border-ink-500 pt-3">
                    <button
                      onClick={() => setIsOpen(true)}
                      className="w-full bg-terra-500 hover:bg-terra-700 text-white text-sm py-2 px-3 rounded-caderno-xs transition-colors"
                    >
                      Open Debug Panel
                    </button>
                  </div>

                  <div className="text-xs text-ink-400 text-center">
                    Press Ctrl+Shift+D to toggle
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Debug Panel */}
      <DebugPanel isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </>
  )
}

// Quick debug utilities for development
export const debugUtils = {
  // Log current component state
  logState: (componentName: string, state: any) => {
    logger.debug('ui', `${componentName} state`, state, componentName)
  },

  // Log performance metrics
  logPerformance: (componentName: string, metrics: any) => {
    logger.info('performance', `${componentName} metrics`, metrics, componentName)
  },

  // Log user interactions
  logInteraction: (componentName: string, action: string, data?: any) => {
    logger.userAction(action, componentName, data)
  },

  // Log API calls
  logApiCall: (method: string, url: string, data?: any) => {
    logger.apiRequest(method, url, data)
  },

  // Log errors with context
  logError: (componentName: string, error: Error, context?: any) => {
    logger.error('ui', `Error in ${componentName}`, context, componentName, error)
  },

  // Create performance timer
  startTimer: (category: string, action: string, component?: string) => {
    return logger.startTimer(category, action, component)
  }
}

export default DebugTrigger
